import { useState, useMemo, useEffect } from "react";
import "./MultiStepForm.css";
import { validateStep } from "../../utils/pageValidator";
import { flattenSteps } from "../../utils/utils";

export default function MultiStepForm({ steps, ProgressBar, validateFormStep = true }) {
    const linearSteps = useMemo(() => flattenSteps(steps), [steps]);

    const initialStepsState = useMemo(() => {
        return linearSteps.map((_, index) => ({
            stepIndex: index,
            completed: false,
            isCurrent: index === 0, // Mark the first step as the current step
            isAllowed: validateFormStep ? index === 0 : true,
        }));
    }, [linearSteps, validateFormStep]);

    const [stepsState, setStepsState] = useState(initialStepsState);

    const currentStepIndex = stepsState.findIndex((step) => step.isCurrent);

    useEffect(() => {
        setStepsState((prevState) => {
            return initialStepsState.map((newStep) => {
                const existingStep = prevState.find((step) => step.stepIndex === newStep.stepIndex);
                return existingStep
                    ? { ...newStep, ...existingStep } // Merge existing state with new state
                    : newStep; // Use new state for new steps
            });
        });
    }, [initialStepsState]);

    const prevStep = () => {
        if (currentStepIndex <= 0) {
            console.warn("Cannot go back. Already at the first step.");
            return;
        }

        setStepsState((prevState) =>
            prevState.map((step) => ({
                ...step,
                isCurrent: step.stepIndex === currentStepIndex - 1,
            }))
        );
        document.querySelector(".app-header").scrollIntoView({ behavior: "smooth", block: "center" });

    };

    const nextStep = () => {
        if (currentStepIndex >= stepsState.length - 1) {
            console.warn("Cannot proceed. Already at the last step.");
            return; 
        }

        if (validateFormStep && !validateStep()) {
            removeComplete();
            return;
        }
        setStepsState((prevState) =>
            prevState.map((step) => ({
                ...step,
                completed: step.stepIndex === currentStepIndex ? true : step.completed,
                isCurrent: step.stepIndex === currentStepIndex + 1,
                isAllowed: step.isAllowed || step.stepIndex <= currentStepIndex + 1,
            }))
        );
        document.querySelector(".app-header").scrollIntoView({ behavior: "smooth", block: "center" });
    };

    // Function to remove complete label when step gets an error
    const removeComplete = () => {
        setStepsState((prevState) =>
            prevState.map((step) => ({
                ...step,
                completed: step.stepIndex === currentStepIndex ? false : step.completed,
                isAllowed: !step.completed && step.stepIndex !== currentStepIndex ? false : step.isAllowed,
            }))
        )
    }

    return (
        <>
            {ProgressBar && (
                <ProgressBar
                    steps={steps}
                    currentStep={currentStepIndex}
                    setCurrentStep={(index) =>
                        setStepsState((prevState) =>
                            prevState.map((step) => ({
                                ...step,
                                isCurrent: step.stepIndex === index && step.isAllowed,
                            }))
                        )
                    }
                    stepCompletion={stepsState.map((step) => step.completed)}
                    stepAllowed={stepsState.map((step) => step.isAllowed)}
                />
            )}
            <form className="multistep-form-container">
                <div className="step-container">
                    {linearSteps[currentStepIndex].content}
                </div>
                <div className="button-container">
                    <div className="button-back">
                        {currentStepIndex > 0 && (
                            <button type="button" onClick={prevStep}>
                                Back
                            </button>
                        )}
                    </div>
                    <div className="button-next">
                        {currentStepIndex < linearSteps.length - 1 && (
                            <button type="button" onClick={nextStep}>
                                Next
                            </button>
                        )}
                    </div>
                </div>
            </form>
        </>
    );
}